import { Injectable, inject } from '@angular/core';
import {
  AbEntrySearchService,
  LeadSearchService,
} from '@maximizer/core/global-search/data-access';
import {
  AbEntrySearch,
  LeadSearch,
  LeadStatus,
} from '@maximizer/core/shared/domain';
import {
  TrackerService,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import {
  AbEntryType,
  EmailEntryType,
  ENTRY_SEARCH_GROUP_ORDER,
  EntrySearchGroupType,
  EntrySearchResult,
  EntrySearchResultGroup,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookEmail,
} from '@maximizer/outlook/shared/domain';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';

const ENTRY_SEARCH_RESULTS_LIMIT = 100;

@Injectable()
export class EntrySearchService {
  private readonly abentrySearchService = inject(AbEntrySearchService);
  private readonly leadSearchService = inject(LeadSearchService);
  private readonly tracker = inject(TrackerService);
  private readonly translate = inject(TranslateService);
  private readonly verticalService = inject(VerticalService);

  toOutlookEmail(entry: EntrySearchResult): OutlookEmail {
    return {
      id: entry.id,
      emailAddress: entry.emailAddress,
      displayName: entry.displayName,
      type: entry.type,
      abentryType: entry.abentryType,
      parentKey: entry.parentKey,
    };
  }

  entryTypeLabel(
    type: EmailEntryType | undefined,
    abentryType?: AbEntryType,
  ): string {
    if (type === EntryType.Lead) {
      return this.translate.instant('outlook.entries.lead');
    }
    if (abentryType === EntryType.Individual) {
      return this.verticalService.translateVertical('individual');
    }
    if (abentryType === EntryType.Company) {
      return this.translate.instant('outlook.entries.company');
    }
    return this.translate.instant('outlook.entries.contact');
  }

  searchEntries(
    term: string,
    canReadLeads: boolean,
  ): Observable<EntrySearchResultGroup[]> {
    return forkJoin({
      abEntries: this.searchAbEntriesSafely(term, ENTRY_SEARCH_RESULTS_LIMIT),
      leads: this.searchLeadsSafely(
        term,
        canReadLeads,
        ENTRY_SEARCH_RESULTS_LIMIT,
      ),
    }).pipe(
      map(({ abEntries, leads }) => this.mapSearchResults(abEntries, leads)),
      tap(() => this.trackSearchCompleted(true)),
      catchError((error) => {
        this.trackSearchCompleted(false);
        throw error;
      }),
    );
  }

  private searchAbEntriesSafely(
    term: string,
    take?: number,
  ): Observable<AbEntrySearch[]> {
    return this.abentrySearchService.search(term, true, take).pipe(
      catchError((error) => {
        console.error('Error fetching AbEntries:', error);
        return of([] as AbEntrySearch[]);
      }),
    );
  }

  private searchLeadsSafely(
    term: string,
    canReadLeads: boolean,
    take?: number,
  ): Observable<LeadSearch[]> {
    if (!canReadLeads) {
      return of([] as LeadSearch[]);
    }

    return this.leadSearchService.search(term, true, take).pipe(
      catchError((error) => {
        console.error('Error fetching Leads:', error);
        return of([] as LeadSearch[]);
      }),
    );
  }

  trackEntryDetailsOpened(entry: EntrySearchResult): void {
    this.tracker.trackEvent({
      module: EventModule.Entry,
      journey: EventJourney.SearchDetails,
      action: EventAction.Clicked,
      metadata: {
        entry: this.resolveEntryType(entry),
      },
    });
  }

  private resolveEntryType(
    entry: EntrySearchResult,
  ): AbEntryType | EmailEntryType {
    return entry.abentryType ?? entry.type;
  }

  private trackSearchCompleted(success: boolean): void {
    this.tracker.trackEvent({
      module: EventModule.Entry,
      journey: EventJourney.Search,
      action: EventAction.Completed,
      metadata: {
        result: success ? EventMetadata.Success : EventMetadata.Failed,
      },
    });
  }

  private mapSearchResults(
    abEntries: AbEntrySearch[],
    leads: LeadSearch[],
  ): EntrySearchResultGroup[] {
    const activeLeads = leads.filter(
      (lead) => lead.status !== LeadStatus.Converted,
    );

    return this.groupResults(
      abEntries
        .map((abEntry) => this.mapAbEntryToSearchResult(abEntry))
        .concat(activeLeads.map((lead) => this.mapLeadToSearchResult(lead))),
    );
  }

  private groupResults(results: EntrySearchResult[]): EntrySearchResultGroup[] {
    const resultsByGroup = new Map<EntrySearchGroupType, EntrySearchResult[]>();

    for (const result of results) {
      const groupType = this.resolveGroupType(result);
      const group = resultsByGroup.get(groupType) ?? [];
      group.push(result);
      resultsByGroup.set(groupType, group);
    }

    return ENTRY_SEARCH_GROUP_ORDER.filter((groupType) =>
      resultsByGroup.has(groupType),
    ).map((groupType) => ({
      type: groupType,
      results: resultsByGroup.get(groupType) ?? [],
    }));
  }

  private resolveGroupType(result: EntrySearchResult): EntrySearchGroupType {
    if (result.type === EntryType.Lead) {
      return 'leads';
    }

    switch (result.abentryType) {
      case EntryType.Individual:
        return 'individuals';
      case EntryType.Contact:
        return 'contacts';
      default:
        return 'companies';
    }
  }

  private mapAbEntryToSearchResult(abEntry: AbEntrySearch): EntrySearchResult {
    const abentryType = abEntry.type.toLowerCase() as AbEntryType;
    const isCompany = abentryType === EntryType.Company;

    return {
      id: abEntry.key ?? '',
      displayName: isCompany ? abEntry.companyName : abEntry.name,
      emailAddress: abEntry.email.toLowerCase(),
      type: 'abentry',
      abentryType,
      parentKey: abEntry.parentKey ?? undefined,
      companyName: abEntry.companyName,
      jobTitle: isCompany ? undefined : abEntry.position,
      phoneNumber: this.firstAvailablePhoneNumber(abEntry),
      lastContactDate: abEntry.lastContactDateValue,
    };
  }

  private firstAvailablePhoneNumber(
    abEntry: AbEntrySearch,
  ): string | undefined {
    return [abEntry.phone, abEntry.phone1, abEntry.phone2, abEntry.phone3].find(
      (phoneNumber) => !!phoneNumber?.trim(),
    );
  }

  private mapLeadToSearchResult(lead: LeadSearch): EntrySearchResult {
    return {
      id: lead.key,
      displayName: lead.name,
      emailAddress: lead.email.toLowerCase(),
      type: 'lead',
      companyName: lead.companyName,
      jobTitle: lead.position,
      phoneNumber: lead.phone1 || lead.phone2 || undefined,
      lastModifiedDate: lead.lastModifiedDate,
      leadStatus: lead.status,
    };
  }
}
